import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Container } from 'reactstrap';
import NavMenu from './NavMenu';
import CTSidebar from '../CTSidebar';
import { ProSidebarProvider } from 'react-pro-sidebar';
import PropTypes from 'prop-types';
import localSettingsManager from '../localSettings/localSettingsManager';
import * as settingConstants from '../constants/settingConstants';
import { useSelector } from 'react-redux';
import AuthUtil from './Utils/AuthUtil';
import { Backdrop, Box, CircularProgress } from '@mui/material';
import UserApiService from '../api/UserApiService';
import ImpersonationBanner from './ImpersonationBanner';
import { useSnackbar } from './Common/SnackbarProvider';

const Layout = (props) => {
    const [navMenuCollapsed, setNavMenuCollapsed] = useState(localSettingsManager.getSettingValue(settingConstants.navBarCollapsedSetting, false) === 'true');
    const { children } = props;
    // App no longer drills a setSnackbarProperties prop down -- it moved to SnackbarProvider --
    // but NavMenu, ImpersonationBanner and the modals below them still take it as a prop. Read
    // the provider's compatibility shim so those keep working instead of receiving undefined.
    const { setSnackbarProperties } = useSnackbar();
    function toggleNavMenu () {
        setNavMenuCollapsed(!navMenuCollapsed);
        localSettingsManager.setSettingValue(settingConstants.navBarCollapsedSetting, !navMenuCollapsed);
    }

    const userApiService = new UserApiService();

    const userToken = useSelector((state) => state.correcttrack.userToken);
    const isAuthorized = useMemo(() => AuthUtil.isAuthorized(userToken), [userToken]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const isPrintTaskPage = useMemo(() => window.location.href.includes('/PrintTask/'), [window.location.href]);
    const isTokenExpired = useMemo(() => AuthUtil.isTokenExpired(userToken), [userToken]);
    const showLayout = useMemo(() => !isAuthorized || (isAuthorized && !isTokenExpired), [isAuthorized, isTokenExpired]);

    useEffect(() => {
        if(isAuthorized && isTokenExpired) {
            userApiService.apiPing();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthorized, isTokenExpired]);

    const showSidebar = isAuthorized && !isPrintTaskPage;
    // react-pro-sidebar defaults: expanded 250px, collapsed 80px
    const sidebarWidth = showSidebar ? (navMenuCollapsed ? 80 : 250) : 0;

    const showHeader = isAuthorized && !isPrintTaskPage;
    const headerRef = useRef(null);

    // The sidebar and every full-height page need to know where the header ends. That used to be
    // the constant 56px of the AppBar toolbar, which stopped being true once the impersonation
    // banner could sit above it -- and the banner's height varies with wrapping, so no constant
    // works. Measure the header instead and publish it as --ct-header-h for them to read.
    useEffect(() => {
        const root = document.documentElement;
        const element = headerRef.current;

        if (!element) {
            root.style.setProperty('--ct-header-h', '0px');
            return undefined;
        }

        const apply = () => root.style.setProperty('--ct-header-h', `${element.offsetHeight}px`);
        apply();

        const observer = new ResizeObserver(apply);
        observer.observe(element);
        return () => observer.disconnect();
    }, [showHeader, showLayout]);

    return (
        <>
            {
                showLayout ?
                    (<div>
                    {/*
                      * One sticky container rather than two. The banner and the AppBar were each
                      * sticky at top: 0, so they stacked correctly only until the page scrolled --
                      * then both pinned to the same offset and the AppBar, on the higher z-index,
                      * slid over the banner. Sticking the pair as a unit lets them stack normally.
                      */}
                    {showHeader && (
                        <Box
                            ref={headerRef}
                            sx={{ position: 'sticky', top: 0, zIndex: (theme) => theme.zIndex.drawer + 2 }}
                        >
                            <ImpersonationBanner setSnackbarProperties={setSnackbarProperties} />
                            <NavMenu toggleNavMenu={toggleNavMenu} setSnackbarProperties={setSnackbarProperties} />
                        </Box>
                    )}
                    {showSidebar && (
                        <ProSidebarProvider>
                            <CTSidebar navMenuCollapsed={navMenuCollapsed} />
                        </ProSidebarProvider>
                    )}
                    <div style={{ marginLeft: sidebarWidth, transition: 'margin-left 0.3s ease' }}>
                        <Container tag={'main'}>
                            {children}
                        </Container>
                    </div>
                </div>)
                    :
                    (<Backdrop
                        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                        open={!showLayout}
                    >
                        <CircularProgress color='inherit' />
                    </Backdrop>)
            }
        </>

        );
};

Layout.propTypes = {
    children: PropTypes.object.isRequired,
};

export default Layout;
